import * as Schema from "effect/Schema";
import { CommandId, ThreadId } from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

/**
 * A usage limit stops a conversation without being the conversation's fault.
 * The engine tells the server which subscription is limited and when it
 * resets; the server keeps that with the conversation (one record per
 * conversation, `usage-limit:<threadId>`), so Cleo can say it, hold waiting
 * messages instead of failing them, and continue when the limit resets.
 */
export const USAGE_LIMIT_ACTIVITY_KIND = "usage-limit.reached";
export const usageLimitActivityId = (threadId: string) => `usage-limit:${threadId}`;

/** What an engine reports with the error or warning that names the limit. */
export const UsageLimitSignal = Schema.Struct({
  engine: Schema.Literals(["claude", "codex"]),
  /** The window that ran out, in words ("5-hour", "weekly"). */
  window: Schema.optional(Schema.String),
  /** When it resets, ISO time; absent when the engine did not say. */
  resetsAt: Schema.optional(Schema.String),
  /**
   * The engine holds the turn itself until the reset (Claude parks it), so
   * there is nothing for Cleo to resume and the queue is not gated.
   */
  parked: Schema.optional(Schema.Boolean),
});
export type UsageLimitSignal = typeof UsageLimitSignal.Type;

/**
 * `ask`: nothing happens at the reset until the person chooses; `resume`:
 * continue the conversation at the reset; `wait`: the person will do it.
 */
export const UsageLimitChoice = Schema.Literals(["ask", "resume", "wait"]);
export type UsageLimitChoice = typeof UsageLimitChoice.Type;

export const UsageLimitRecord = Schema.Struct({
  engine: Schema.Literals(["claude", "codex"]),
  /** The engine instance (the subscription) that is limited, when known. */
  instanceId: Schema.optional(Schema.String),
  window: Schema.optional(Schema.String),
  resetsAt: Schema.NullOr(Schema.String),
  /** When Cleo acts: the reset, or now when the person chose to resume now. */
  resumeAt: Schema.NullOr(Schema.String),
  turnId: Schema.NullOr(Schema.String),
  parked: Schema.Boolean,
  /**
   * `limited`: the reset has not come; `reset`: it has and nothing resumed the
   * conversation yet (the person is offered to); `resumed`: Cleo continued it;
   * `cleared`: the person dismissed it or the engine carried on by itself.
   */
  state: Schema.Literals(["limited", "reset", "resumed", "cleared"]),
  choice: UsageLimitChoice,
  message: Schema.String,
  recordedAt: Schema.String,
  resumedAt: Schema.optional(Schema.String),
  /**
   * The account (engine instance) the person chose to continue on, on his
   * click. Set by `thread.usage-limit.update` action `switch`; the server then
   * sends the continuation on that account (the ordinary account switch of a
   * turn start) and records `resumed`. Absent: continue on the same account.
   */
  switchTo: Schema.optional(ProviderInstanceId),
});
export type UsageLimitRecord = typeof UsageLimitRecord.Type;

export const USAGE_LIMIT_ENGINE_LABEL = {
  claude: "Claude",
  codex: "ChatGPT (Codex)",
} as const;

/** Which subscription is limited and until when; the caller formats the time for its reader. */
export function usageLimitSentence(
  record: Pick<UsageLimitRecord, "engine" | "window" | "resetsAt">,
  formatTime: (iso: string) => string = (iso) => iso,
): string {
  const who = USAGE_LIMIT_ENGINE_LABEL[record.engine];
  const window = record.window ? ` ${record.window}` : "";
  return record.resetsAt
    ? `Your ${who}${window} usage limit is reached. It resets ${formatTime(record.resetsAt)}.`
    : `Your ${who}${window} usage limit is reached. The reset time was not reported.`;
}

export function readUsageLimit(
  activities: ReadonlyArray<{ readonly kind: string; readonly payload: unknown }>,
): UsageLimitRecord | undefined {
  for (let at = activities.length - 1; at >= 0; at--) {
    const activity = activities[at]!;
    if (activity.kind !== USAGE_LIMIT_ACTIVITY_KIND) continue;
    const decoded = Schema.decodeUnknownOption(UsageLimitRecord)(activity.payload);
    if (decoded._tag === "Some") return decoded.value;
  }
  return undefined;
}

/**
 * Whether waiting messages must keep waiting: the conversation is limited
 * and the time to act has not come. A limit with no reset time holds them
 * until the person says to try again.
 */
export function usageLimitHoldsQueue(record: UsageLimitRecord | undefined, nowMs: number): boolean {
  if (record === undefined || record.state !== "limited" || record.parked) return false;
  const at = record.resumeAt ?? record.resetsAt;
  if (at === null) return true;
  const atMs = Date.parse(at);
  return !Number.isFinite(atMs) || atMs > nowMs;
}

/**
 * What the sidebar needs to say "Limited" with the reset time: the open limit
 * of a conversation, folded from its record. Never carries the message.
 */
export const ThreadUsageLimitShell = Schema.Struct({
  engine: Schema.Literals(["claude", "codex"]),
  state: Schema.Literals(["limited", "reset"]),
  resetsAt: Schema.NullOr(Schema.String),
});
export type ThreadUsageLimitShell = typeof ThreadUsageLimitShell.Type;

/** The open, Cleo-resumable limit of a conversation; a parked one is the engine's own wait and still shows. */
export function usageLimitShellOf(
  record: UsageLimitRecord | undefined,
): ThreadUsageLimitShell | null {
  if (record === undefined || (record.state !== "limited" && record.state !== "reset")) return null;
  return { engine: record.engine, state: record.state, resetsAt: record.resetsAt };
}

export const ThreadUsageLimitUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.usage-limit.update"),
  commandId: CommandId,
  threadId: ThreadId,
  /**
   * `resume`: continue at the reset; `wait`: do not; `now`: continue as soon
   * as Cleo is free; `switch`: continue now on another account (`instanceId`);
   * `reset`, `resumed` and `cleared` are the server recording
   * what happened (`cleared` is also the person dismissing the notice).
   */
  action: Schema.Literals(["resume", "wait", "now", "switch", "reset", "resumed", "cleared"]),
  /**
   * `switch` only: the other account of the same engine to continue on. The
   * switch happens only on the person's click (PC10); the record keeps it so
   * the server sends once, under deterministic ids.
   */
  instanceId: Schema.optional(ProviderInstanceId),
  createdAt: Schema.String,
});
export type ThreadUsageLimitUpdateCommand = typeof ThreadUsageLimitUpdateCommand.Type;

/** The record is past its reset and Cleo should act on it now (sweep rule, shared with tests). */
export function usageLimitDue(record: UsageLimitRecord, nowMs: number): boolean {
  if (record.parked) return false;
  if (record.state === "reset") return true;
  // The person chose an account with capacity: act now, not at the old reset.
  if (record.state === "limited" && record.switchTo !== undefined) return true;
  if (record.state !== "limited" || record.resumeAt === null) return false;
  const atMs = Date.parse(record.resumeAt);
  return Number.isFinite(atMs) && atMs <= nowMs;
}
